import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, BarSeries, LineSeries, AreaSeries, BaselineSeries, HistogramSeries } from 'lightweight-charts';

const CHART_TYPES = [
  { id: 'candlestick', label: 'Candlestick' },
  { id: 'bar', label: 'OHLC Bar' },
  { id: 'line', label: 'Line' },
  { id: 'area', label: 'Area' },
  { id: 'baseline', label: 'Baseline' },
  { id: 'hollow', label: 'Hollow Candles' },
];

const TIMEFRAMES = [
  { id: '15m', label: '15m', days: 5, interval: '15min' },
  { id: '1H', label: '1H', days: 30, interval: '1hour' },
  { id: '1D', label: '1D', days: 90, interval: 'daily' },
  { id: '3M', label: '3M', days: 90, interval: 'daily' },
  { id: '1Y', label: '1Y', days: 365, interval: 'daily' },
  { id: 'ALL', label: 'ALL', days: 9999, interval: 'daily' },
];

// Standard Fibonacci retracement levels
const FIB_RETRACEMENT_LEVELS = [
  { level: 0, label: '0%', color: '#787b86' },
  { level: 0.236, label: '23.6%', color: '#f44336' },
  { level: 0.382, label: '38.2%', color: '#ff9800' },
  { level: 0.5, label: '50%', color: '#ffeb3b' },
  { level: 0.618, label: '61.8%', color: '#4caf50' },  // Golden ratio
  { level: 0.786, label: '78.6%', color: '#2196f3' },
  { level: 1, label: '100%', color: '#787b86' },
];

// Fibonacci extension levels for price targets
const FIB_EXTENSION_LEVELS = [
  { level: 1.272, label: '127.2%', color: '#9c27b0' },
  { level: 1.414, label: '141.4%', color: '#e91e63' },
  { level: 1.618, label: '161.8%', color: '#00bcd4' },  // Golden ratio extension
  { level: 2.618, label: '261.8%', color: '#009688' },
];

function CandlestickChart({ prices, symbol, onTimeframeChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [chartType, setChartType] = useState('candlestick');
  const [timeframe, setTimeframe] = useState('1D');
  const [showVolume, setShowVolume] = useState(true);
  const [showMA, setShowMA] = useState({ sma20: false, sma50: false, ema12: false });
  const [showFib, setShowFib] = useState({ retraceUp: false, retraceDown: false, extension: false });

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Clear existing chart first
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (e) {
        // Ignore removal errors
      }
      chartRef.current = null;
    }

    // Don't create chart if no data
    if (!prices || prices.length === 0) return;

    let chart = null;
    let handleResize = null;

    try {
      // Create chart
      chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: 'solid', color: 'transparent' },
          textColor: '#9ca3af',
        },
        grid: {
          vertLines: { color: 'rgba(55, 65, 81, 0.5)' },
          horzLines: { color: 'rgba(55, 65, 81, 0.5)' },
        },
        crosshair: {
          mode: 1,
          vertLine: {
            color: 'rgba(74, 222, 128, 0.5)',
            width: 1,
            style: 2,
          },
          horzLine: {
            color: 'rgba(74, 222, 128, 0.5)',
            width: 1,
            style: 2,
          },
        },
        rightPriceScale: {
          borderColor: 'rgba(55, 65, 81, 0.8)',
        },
        timeScale: {
          borderColor: 'rgba(55, 65, 81, 0.8)',
          timeVisible: true,
          secondsVisible: false,
        },
        handleScroll: {
          vertTouchDrag: false,
        },
      });

      chartRef.current = chart;

      // Format price data
      const formattedData = prices.map(p => ({
        time: new Date(p.timestamp).getTime() / 1000,
        open: parseFloat(p.open),
        high: parseFloat(p.high),
        low: parseFloat(p.low),
        close: parseFloat(p.close),
      })).sort((a, b) => a.time - b.time);

      const volumeData = prices.map(p => ({
        time: new Date(p.timestamp).getTime() / 1000,
        value: parseInt(p.volume),
        color: parseFloat(p.close) >= parseFloat(p.open)
          ? 'rgba(74, 222, 128, 0.5)'
          : 'rgba(248, 113, 113, 0.5)',
      })).sort((a, b) => a.time - b.time);

      // Create main series based on chart type
      let series;
      switch (chartType) {
        case 'candlestick':
          series = chart.addSeries(CandlestickSeries, {
            upColor: '#4ade80',
            downColor: '#f87171',
            borderUpColor: '#4ade80',
            borderDownColor: '#f87171',
            wickUpColor: '#4ade80',
            wickDownColor: '#f87171',
          });
          series.setData(formattedData);
          break;

        case 'bar':
          series = chart.addSeries(BarSeries, {
            upColor: '#4ade80',
            downColor: '#f87171',
          });
          series.setData(formattedData);
          break;

        case 'line':
          series = chart.addSeries(LineSeries, {
            color: '#4ade80',
            lineWidth: 2,
          });
          series.setData(formattedData.map(d => ({ time: d.time, value: d.close })));
          break;

        case 'area':
          series = chart.addSeries(AreaSeries, {
            topColor: 'rgba(74, 222, 128, 0.4)',
            bottomColor: 'rgba(74, 222, 128, 0.0)',
            lineColor: '#4ade80',
            lineWidth: 2,
          });
          series.setData(formattedData.map(d => ({ time: d.time, value: d.close })));
          break;

        case 'baseline':
          const avgPrice = formattedData.reduce((acc, d) => acc + d.close, 0) / formattedData.length;
          series = chart.addSeries(BaselineSeries, {
            baseValue: { type: 'price', price: avgPrice },
            topLineColor: '#4ade80',
            topFillColor1: 'rgba(74, 222, 128, 0.3)',
            topFillColor2: 'rgba(74, 222, 128, 0.0)',
            bottomLineColor: '#f87171',
            bottomFillColor1: 'rgba(248, 113, 113, 0.0)',
            bottomFillColor2: 'rgba(248, 113, 113, 0.3)',
          });
          series.setData(formattedData.map(d => ({ time: d.time, value: d.close })));
          break;

        case 'hollow':
          series = chart.addSeries(CandlestickSeries, {
            upColor: 'transparent',
            downColor: '#f87171',
            borderUpColor: '#4ade80',
            borderDownColor: '#f87171',
            wickUpColor: '#4ade80',
            wickDownColor: '#f87171',
          });
          series.setData(formattedData);
          break;

        default:
          series = chart.addSeries(CandlestickSeries, {
            upColor: '#4ade80',
            downColor: '#f87171',
            borderUpColor: '#4ade80',
            borderDownColor: '#f87171',
            wickUpColor: '#4ade80',
            wickDownColor: '#f87171',
          });
          series.setData(formattedData);
      }

      // Add volume if enabled
      if (showVolume && volumeData.length > 0) {
        const volumeSeries = chart.addSeries(HistogramSeries, {
          priceFormat: {
            type: 'volume',
          },
          priceScaleId: 'volume',
        });

        chart.priceScale('volume').applyOptions({
          scaleMargins: {
            top: 0.8,
            bottom: 0,
          },
        });

        volumeSeries.setData(volumeData);
      }

      // Add Moving Averages if enabled
      if (showMA.sma20 && formattedData.length >= 20) {
        const sma20Data = calculateSMA(formattedData, 20);
        const sma20Series = chart.addSeries(LineSeries, {
          color: '#fbbf24',
          lineWidth: 1,
          title: 'SMA 20',
        });
        sma20Series.setData(sma20Data);
      }

      if (showMA.sma50 && formattedData.length >= 50) {
        const sma50Data = calculateSMA(formattedData, 50);
        const sma50Series = chart.addSeries(LineSeries, {
          color: '#8b5cf6',
          lineWidth: 1,
          title: 'SMA 50',
        });
        sma50Series.setData(sma50Data);
      }

      if (showMA.ema12 && formattedData.length >= 12) {
        const ema12Data = calculateEMA(formattedData, 12);
        const ema12Series = chart.addSeries(LineSeries, {
          color: '#ec4899',
          lineWidth: 1,
          title: 'EMA 12',
        });
        ema12Series.setData(ema12Data);
      }

      // Calculate swing high/low for Fibonacci
      const { swingHigh, swingLow, highIndex, lowIndex } = findSwingPoints(formattedData);
      const priceRange = swingHigh - swingLow;

      // Fibonacci Retracement - Uptrend (Low to High)
      // Used when price is in uptrend and you expect a pullback to find support
      if (showFib.retraceUp && formattedData.length >= 5) {
        FIB_RETRACEMENT_LEVELS.forEach(fib => {
          // In uptrend retracement: levels are measured DOWN from the high
          const price = swingHigh - (priceRange * fib.level);
          const fibSeries = chart.addSeries(LineSeries, {
            color: fib.color,
            lineWidth: 1,
            lineStyle: 2, // Dashed
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          // Draw horizontal line across entire chart
          fibSeries.setData([
            { time: formattedData[0].time, value: price },
            { time: formattedData[formattedData.length - 1].time, value: price },
          ]);
        });
      }

      // Fibonacci Retracement - Downtrend (High to Low)
      // Used when price is in downtrend and you expect a rally to find resistance
      if (showFib.retraceDown && formattedData.length >= 5) {
        FIB_RETRACEMENT_LEVELS.forEach(fib => {
          // In downtrend retracement: levels are measured UP from the low
          const price = swingLow + (priceRange * fib.level);
          const fibSeries = chart.addSeries(LineSeries, {
            color: fib.color,
            lineWidth: 1,
            lineStyle: 2, // Dashed
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          fibSeries.setData([
            { time: formattedData[0].time, value: price },
            { time: formattedData[formattedData.length - 1].time, value: price },
          ]);
        });
      }

      // Fibonacci Extensions - Price targets beyond the swing
      if (showFib.extension && formattedData.length >= 5) {
        const isUptrend = highIndex > lowIndex;

        FIB_EXTENSION_LEVELS.forEach(fib => {
          let price;
          if (isUptrend) {
            // Uptrend: extensions project above the high
            price = swingLow + (priceRange * fib.level);
          } else {
            // Downtrend: extensions project below the low
            price = swingHigh - (priceRange * fib.level);
          }

          const fibSeries = chart.addSeries(LineSeries, {
            color: fib.color,
            lineWidth: 1,
            lineStyle: 3, // Dotted
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          fibSeries.setData([
            { time: formattedData[0].time, value: price },
            { time: formattedData[formattedData.length - 1].time, value: price },
          ]);
        });
      }

      // Fit content
      chart.timeScale().fitContent();

      // Handle resize
      handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);
      handleResize();
    } catch (error) {
      console.error('Error creating chart:', error);
    }

    return () => {
      if (handleResize) {
        window.removeEventListener('resize', handleResize);
      }
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
        chartRef.current = null;
      }
    };
  }, [prices, chartType, showVolume, showMA, showFib]);

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    const selectedTimeframe = TIMEFRAMES.find(t => t.id === tf);
    if (selectedTimeframe && onTimeframeChange) {
      onTimeframeChange(selectedTimeframe.days, selectedTimeframe.interval);
    }
  };

  const toggleMA = (ma) => {
    setShowMA(prev => ({ ...prev, [ma]: !prev[ma] }));
  };

  const toggleFib = (fib) => {
    setShowFib(prev => ({ ...prev, [fib]: !prev[fib] }));
  };

  const hasData = prices && prices.length > 0;

  return (
    <div className="candlestick-chart-wrapper">
      <div className="chart-controls">
        <div className="control-group">
          <span className="control-label">Chart Type</span>
          <div className="chart-type-buttons">
            {CHART_TYPES.map(type => (
              <button
                key={type.id}
                className={chartType === type.id ? 'active' : ''}
                onClick={() => setChartType(type.id)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <span className="control-label">Timeframe</span>
          <div className="timeframe-buttons">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf.id}
                className={timeframe === tf.id ? 'active' : ''}
                onClick={() => handleTimeframeChange(tf.id)}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <span className="control-label">Overlays</span>
          <div className="overlay-buttons">
            <button
              className={showVolume ? 'active' : ''}
              onClick={() => setShowVolume(!showVolume)}
            >
              Volume
            </button>
            <button
              className={showMA.sma20 ? 'active' : ''}
              onClick={() => toggleMA('sma20')}
            >
              SMA 20
            </button>
            <button
              className={showMA.sma50 ? 'active' : ''}
              onClick={() => toggleMA('sma50')}
            >
              SMA 50
            </button>
            <button
              className={showMA.ema12 ? 'active' : ''}
              onClick={() => toggleMA('ema12')}
            >
              EMA 12
            </button>
          </div>
        </div>

        <div className="control-group">
          <span className="control-label">Fibonacci</span>
          <div className="fib-buttons">
            <button
              className={showFib.retraceUp ? 'active fib-up' : ''}
              onClick={() => toggleFib('retraceUp')}
              title="Retracement levels for uptrend - shows support during pullbacks"
            >
              Fib Retrace (Up)
            </button>
            <button
              className={showFib.retraceDown ? 'active fib-down' : ''}
              onClick={() => toggleFib('retraceDown')}
              title="Retracement levels for downtrend - shows resistance during rallies"
            >
              Fib Retrace (Down)
            </button>
            <button
              className={showFib.extension ? 'active fib-ext' : ''}
              onClick={() => toggleFib('extension')}
              title="Extension levels - price targets beyond the swing"
            >
              Fib Extension
            </button>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="candlestick-empty" style={{ height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>No price data available for this timeframe</p>
        </div>
      ) : (
        <div
          ref={chartContainerRef}
          className="candlestick-container"
          style={{ height: '450px' }}
        />
      )}

      <div className="chart-legend">
        <span className="legend-item">
          <span className="legend-color" style={{ background: '#4ade80' }}></span>
          Bullish
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{ background: '#f87171' }}></span>
          Bearish
        </span>
        {showMA.sma20 && (
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#fbbf24' }}></span>
            SMA 20
          </span>
        )}
        {showMA.sma50 && (
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#8b5cf6' }}></span>
            SMA 50
          </span>
        )}
        {showMA.ema12 && (
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#ec4899' }}></span>
            EMA 12
          </span>
        )}
      </div>

      {(showFib.retraceUp || showFib.retraceDown || showFib.extension) && (
        <div className="fib-legend">
          <span className="fib-legend-title">Fibonacci Levels:</span>
          {(showFib.retraceUp || showFib.retraceDown) && (
            <div className="fib-legend-section">
              <span className="fib-section-title">Retracement:</span>
              {FIB_RETRACEMENT_LEVELS.map(fib => (
                <span key={fib.label} className="legend-item">
                  <span className="legend-color" style={{ background: fib.color }}></span>
                  {fib.label}
                </span>
              ))}
            </div>
          )}
          {showFib.extension && (
            <div className="fib-legend-section">
              <span className="fib-section-title">Extension:</span>
              {FIB_EXTENSION_LEVELS.map(fib => (
                <span key={fib.label} className="legend-item">
                  <span className="legend-color" style={{ background: fib.color }}></span>
                  {fib.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Find swing high and low points for Fibonacci calculations
function findSwingPoints(data) {
  if (data.length === 0) {
    return { swingHigh: 0, swingLow: 0, highIndex: 0, lowIndex: 0 };
  }

  let swingHigh = data[0].high;
  let swingLow = data[0].low;
  let highIndex = 0;
  let lowIndex = 0;

  for (let i = 1; i < data.length; i++) {
    if (data[i].high > swingHigh) {
      swingHigh = data[i].high;
      highIndex = i;
    }
    if (data[i].low < swingLow) {
      swingLow = data[i].low;
      lowIndex = i;
    }
  }

  return { swingHigh, swingLow, highIndex, lowIndex };
}

// Calculate Simple Moving Average
function calculateSMA(data, period) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    const sum = data.slice(i - period + 1, i + 1).reduce((acc, d) => acc + d.close, 0);
    result.push({
      time: data[i].time,
      value: sum / period,
    });
  }
  return result;
}

// Calculate Exponential Moving Average
function calculateEMA(data, period) {
  const result = [];
  const k = 2 / (period + 1);

  // Start with SMA for first value
  let ema = data.slice(0, period).reduce((acc, d) => acc + d.close, 0) / period;
  result.push({ time: data[period - 1].time, value: ema });

  for (let i = period; i < data.length; i++) {
    ema = data[i].close * k + ema * (1 - k);
    result.push({ time: data[i].time, value: ema });
  }
  return result;
}

export default CandlestickChart;
